import type {
  CarrierResolution,
  ContainerDetail,
  ContainerStatus,
  DashboardContainer,
  DepotCheckResponse,
  DepotOption,
  ImportParseResult,
  ImportTrackInput,
  ImportTrackResult,
  PdfImportResult,
  RefreshContainerResult,
  Role,
  TerminalOption,
} from '@ctp/shared';

export type {
  TerminalOption,
  ContainerDetail,
  DashboardContainer,
  RefreshContainerResult,
  ImportParseResult,
  ImportTrackInput,
  ImportTrackResult,
  PdfImportResult,
};

export const API_BASE = (import.meta.env.VITE_API_BASE_URL as string) || '/api';

export interface SessionUser {
  id: string;
  orgId: string;
  email: string;
  role: Role;
  name: string | null;
  firstName: string | null;
  lastName: string | null;
}

export interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  user: SessionUser;
}

export type TrackingResult =
  | { status: 'found'; data: ContainerStatus }
  | { status: 'not_found'; carrierCode: string }
  | { status: 'error'; carrierCode: string; message: string };

export interface LiteTrackResult {
  containerNumber: string;
  valid: boolean;
  resolution: CarrierResolution;
  needsCarrierInput: boolean;
  carrierCode?: string;
  result?: TrackingResult;
  /** Terminal-Vorgabe wich vom tatsächlichen Standort ab (HHLA terminalübergreifend) — H6b. */
  terminalMismatch?: { selectedLabel: string; actual: string };
}

export interface OrgUser {
  id: string;
  email: string;
  name: string | null;
  role: Role;
  status: string;
  createdAt: string;
}

export class ApiError extends Error {
  constructor(
    readonly httpStatus: number,
    message: string,
    /** Maschinenlesbarer Code aus der Fehlerantwort (z. B. 'trial_exhausted'). */
    readonly code?: string,
  ) {
    super(message);
  }
}

/** Plan-/Trial-Stand (spiegelt EntitlementService.status der API). */
export interface BillingStatus {
  stripeConfigured: boolean;
  plan: 'none' | 's' | 'l';
  hasActivePlan: boolean;
  subscriptionStatus: string | null;
  seats: number | null;
  periodEnd: string | null;
  limit: number | null;
  used: number;
  remaining: number;
  trialUsed: number;
  trialQuota: number;
  trialRemaining: number;
  /** Anstehender geplanter Plan-Wechsel (Downgrade zum Periodenende), sonst null. */
  scheduledPlan: 's' | 'l' | null;
  scheduledChangeAt: string | null;
  /**
   * Premium-Abrechnung (EPIC P). Optional — solange das Backend (P3/P5) sie noch nicht liefert,
   * greifen die Defaults im UI (Modus 'wallet', keine PAYG-Zahlungsweise, 0 Premium-Container).
   */
  premiumBillingMode?: PremiumBillingMode;
  /** PAYG-Zahlungsweise hinterlegt (metered Subscription aktiv)? */
  premiumPaygOnFile?: boolean;
  /** Anzahl im laufenden Monat aktivierter Premium-Container (informativ). */
  premiumThisMonth?: number;
  /** Aktueller Wallet-Saldo in Cent (für die Premium-Abrechnungskarte). */
  walletBalanceCents?: number;
}

export type PremiumBillingMode = 'wallet' | 'payg';

/** Antwort von POST /api/premium/activate (EPIC P, P4). */
export interface PremiumActivateResult {
  trackedContainerId: string;
  alreadyPremium: boolean;
  charged: boolean;
  amountCents: number;
}

async function request<T>(
  method: string,
  path: string,
  opts: { body?: unknown; token?: string | null } = {},
): Promise<T> {
  const headers: Record<string, string> = {};
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  if (opts.token) headers.Authorization = `Bearer ${opts.token}`;
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  });
  if (!res.ok) {
    let msg = res.statusText;
    let code: string | undefined;
    try {
      const j = (await res.json()) as { message?: string | string[]; code?: string };
      if (j.message) msg = Array.isArray(j.message) ? j.message.join(', ') : j.message;
      if (j.code) code = j.code;
    } catch {
      /* keine JSON-Fehlerantwort */
    }
    throw new ApiError(res.status, msg, code);
  }
  return (res.status === 204 ? undefined : await res.json()) as T;
}

export const api = {
  get: <T>(path: string, token?: string | null): Promise<T> => request<T>('GET', path, { token }),
  post: <T>(path: string, body?: unknown, token?: string | null): Promise<T> =>
    request<T>('POST', path, { body, token }),
  patch: <T>(path: string, body?: unknown, token?: string | null): Promise<T> =>
    request<T>('PATCH', path, { body, token }),
  del: <T>(path: string, token?: string | null): Promise<T> =>
    request<T>('DELETE', path, { token }),
};

/** Eigenes Profil: PATCH /me vervollständigt Vor-/Nachname (z. B. beim ersten Login). */
export const meApi = {
  updateProfile: (
    body: { firstName: string; lastName: string },
    token?: string | null,
  ): Promise<SessionUser> => api.patch<SessionUser>('/me', body, token),
};

/** Auswählbare Terminals für die gezielte Containerauskunft (H6). */
export const terminalsApi = {
  list: (token?: string | null): Promise<TerminalOption[]> =>
    api.get<TerminalOption[]>('/lite/terminals', token),
};

/**
 * Gespeicherte Container im Dashboard (ADR-027/ADR-034). `list` liefert die Org-Container;
 * `refresh` erzwingt einen frischen Abruf und zählt gegen das Limit (402 bei vollem Kontingent).
 */
export const containersApi = {
  list: (token?: string | null, state?: 'active' | 'completed'): Promise<DashboardContainer[]> =>
    api.get<DashboardContainer[]>(
      state ? `/containers?state=${state}` : '/containers',
      token,
    ),
  detail: (id: string, token?: string | null): Promise<ContainerDetail> =>
    api.get<ContainerDetail>(`/containers/${encodeURIComponent(id)}`, token),
  refresh: (id: string, token?: string | null): Promise<RefreshContainerResult> =>
    api.post<RefreshContainerResult>(`/containers/${encodeURIComponent(id)}/refresh`, {}, token),
  /** Manueller Abschluss (org-scoped Archiv, EPIC Q). */
  complete: (id: string, token?: string | null): Promise<{ ok: true }> =>
    api.post<{ ok: true }>(`/containers/${encodeURIComponent(id)}/complete`, {}, token),
  /** Reaktivierung eines manuell archivierten Containers (EPIC Q); 409 bei auto-abgeschlossener Reise. */
  reactivate: (id: string, token?: string | null): Promise<{ ok: true }> =>
    api.post<{ ok: true }>(`/containers/${encodeURIComponent(id)}/reactivate`, {}, token),
  remove: (id: string, token?: string | null): Promise<void> =>
    api.del<void>(`/containers/${encodeURIComponent(id)}`, token),
};

/**
 * Leerdepot-Avis-Prüfung (EPIC I). `list` füllt das Dashboard-Dropdown; `check` prüft eine
 * Containernummer an einem Leerdepot (Worker-Delegation, browserbasiert) → grün/rot.
 */
export const depotsApi = {
  list: (token?: string | null): Promise<DepotOption[]> => api.get<DepotOption[]>('/depots', token),
  check: (
    containerNumber: string,
    depotId: string,
    token?: string | null,
    trackedContainerId?: string,
    /** Avisierungsreferenz (I2b) — Pflicht bei Depots mit `requiresReference` (z. B. IGS/HCS). */
    reference?: string,
  ): Promise<DepotCheckResponse> =>
    api.post<DepotCheckResponse>(
      '/depots/check',
      { containerNumber, depotId, trackedContainerId, reference },
      token,
    ),
};

/** XHR-Upload mit echtem Fortschritt (für größere Excel-Dateien); Fehlerformat wie `request()`. */
function uploadFile<T>(
  path: string,
  file: File,
  token?: string | null,
  onProgress?: (pct: number) => void,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API_BASE}${path}`);
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText) as T);
        } catch {
          reject(new ApiError(xhr.status, 'Ungültige Serverantwort'));
        }
        return;
      }
      let msg = xhr.statusText;
      let code: string | undefined;
      try {
        const j = JSON.parse(xhr.responseText) as { message?: string | string[]; code?: string };
        if (j.message) msg = Array.isArray(j.message) ? j.message.join(', ') : j.message;
        if (j.code) code = j.code;
      } catch {
        /* keine JSON-Fehlerantwort */
      }
      reject(new ApiError(xhr.status, msg, code));
    };
    xhr.onerror = () => reject(new ApiError(0, 'Netzwerkfehler beim Upload'));
    const fd = new FormData();
    fd.append('file', file);
    xhr.send(fd);
  });
}

/**
 * Dashboard-Import (Lite/Terminal). `parse` erkennt Container-/Terminal-/Reederei-Spalte einer
 * Excel-/CSV-Liste, `parsePdf` liest Container-Nummer, Reederei und Terminal aus einem PDF-Dokument
 * (B/L, Buchungsbestätigung) — beide liefern nur die Vorschau. `track` speichert die bestätigten
 * Container über den Lite-Pfad (zählt gegen Trial/Limit); keine Datei wird gespeichert.
 */
export const importApi = {
  parse: (
    file: File,
    token?: string | null,
    onProgress?: (pct: number) => void,
  ): Promise<ImportParseResult> =>
    uploadFile<ImportParseResult>('/containers/import/parse', file, token, onProgress),
  parsePdf: (
    file: File,
    token?: string | null,
    onProgress?: (pct: number) => void,
  ): Promise<PdfImportResult> =>
    uploadFile<PdfImportResult>('/containers/import/pdf', file, token, onProgress),
  track: (rows: ImportTrackInput[], token?: string | null): Promise<ImportTrackResult> =>
    api.post<ImportTrackResult>('/containers/import', { rows }, token),
};

/** Billing-Endpunkte (EPIC E). Checkout/Portal nur Org-Admin (serverseitig erzwungen). */
export const billingApi = {
  status: (token?: string | null): Promise<BillingStatus> =>
    api.get<BillingStatus>('/billing/status', token),
  checkout: (plan: 'lite_s' | 'lite_l', token?: string | null): Promise<{ url: string }> =>
    api.post<{ url: string }>('/billing/checkout', { plan }, token),
  portal: (token?: string | null): Promise<{ url: string }> =>
    api.post<{ url: string }>('/billing/portal', {}, token),
  changePlan: (
    plan: 'lite_s' | 'lite_l',
    token?: string | null,
  ): Promise<{ mode: string; effectiveAt: string | null }> =>
    api.post<{ mode: string; effectiveAt: string | null }>('/billing/change-plan', { plan }, token),
  /** Premium-Abrechnungsart der Org setzen (nur Org-Admin, serverseitig erzwungen). */
  setPremiumMode: (mode: PremiumBillingMode, token?: string | null): Promise<BillingStatus> =>
    api.post<BillingStatus>('/billing/premium-mode', { mode }, token),
};

/**
 * Premium-Aktivierung & PAYG-Setup (EPIC P, P4). `activate` belastet 3,50 € netto NUR bei Erfolg
 * (Charge serverseitig, nie aus dem Body); `paygSetup` liefert die Stripe-Checkout-URL für die
 * Erst-Einrichtung der Pay-as-you-go-Zahlungsweise. White-Label: nach außen nur „Premium".
 */
export const premiumApi = {
  activate: (containerNumber: string, token?: string | null): Promise<PremiumActivateResult> =>
    api.post<PremiumActivateResult>('/premium/activate', { containerNumber }, token),
  paygSetup: (token?: string | null): Promise<{ url: string }> =>
    api.post<{ url: string }>('/premium/payg-setup', {}, token),
};
